
import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTeamId } from "@/hooks/useTeamId";
import { TaskType, ColumnType } from "@/components/tasks/TaskManager";

// --- Status mapping: DB ↔ UI ---
type DbStatus = "pending" | "in_progress" | "complete";
type UiStatus = "todo" | "in-progress" | "done";

const dbStatusToUi: Record<DbStatus, UiStatus> = {
  pending: "todo",
  in_progress: "in-progress",
  complete: "done",
};

const uiStatusToDb: Record<UiStatus, DbStatus> = {
  todo: "pending",
  "in-progress": "in_progress",
  done: "complete",
};

// --- Row → TaskType mapper ---
interface DbTaskRow {
  id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  assignee_id: string | null;
  priority: string;
  status: string;
  category: string | null;
  source: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  team_id: string;
  created_by: string;
}

function mapDbRowToTask(row: DbTaskRow): TaskType {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? "",
    dueDate: row.due_date ?? undefined,
    priority: (row.priority as TaskType["priority"]) || "medium",
    status: dbStatusToUi[row.status as DbStatus] ?? "todo",
    completedDate: row.completed_at ?? undefined,
    assignee: row.assignee_id
      ? { id: row.assignee_id, name: "" }
      : undefined,
  };
}

function groupTasksIntoColumns(tasks: TaskType[]): ColumnType[] {
  const todo: TaskType[] = [];
  const inProgress: TaskType[] = [];
  const done: TaskType[] = [];

  for (const task of tasks) {
    if (task.status === "todo") todo.push(task);
    else if (task.status === "in-progress") inProgress.push(task);
    else done.push(task);
  }

  return [
    { id: "todo", title: "To Do", tasks: todo },
    { id: "in-progress", title: "In Progress", tasks: inProgress },
    { id: "done", title: "Done", tasks: done },
  ];
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  priority?: "low" | "medium" | "high";
  dueDate?: string;
  category?: string;
  assigneeId?: string;
}

export const useTaskState = () => {
  const { user } = useAuth();
  const { effectiveTeamId, loading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();

  const queryKey = ["tasks", effectiveTeamId];

  // --- LOAD ---
  const { data: tasks = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!effectiveTeamId) return [];

      const { data, error } = await supabase
        .from("tasks")
        .select(
          "id, title, description, due_date, assignee_id, priority, status, category, source, completed_at, created_at, updated_at, team_id, created_by"
        )
        .eq("team_id", effectiveTeamId)
        .order("status", { ascending: true })
        .order("due_date", { ascending: true, nullsFirst: false });

      if (error) {
        console.error("[useTaskState] Load error:", error.message);
        return [];
      }

      return (data as DbTaskRow[]).map(mapDbRowToTask);
    },
    enabled: !!effectiveTeamId && !!user?.id,
    staleTime: 30_000,
  });

  const columns = useMemo(() => groupTasksIntoColumns(tasks), [tasks]);

  // --- UPDATE ---
  const updateMutation = useMutation({
    mutationFn: async (updatedTask: TaskType) => {
      const dbStatus = uiStatusToDb[updatedTask.status];
      const { error } = await supabase
        .from("tasks")
        .update({
          title: updatedTask.title,
          description: updatedTask.description || null,
          priority: updatedTask.priority,
          status: dbStatus,
          due_date: updatedTask.dueDate || null,
          assignee_id: updatedTask.assignee?.id || null,
        })
        .eq("id", updatedTask.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // --- BATCH UPDATE ---
  const batchUpdateMutation = useMutation({
    mutationFn: async (tasksToUpdate: TaskType[]) => {
      const promises = tasksToUpdate.map((task) => {
        const dbStatus = uiStatusToDb[task.status];
        return supabase
          .from("tasks")
          .update({ status: dbStatus })
          .eq("id", task.id);
      });
      const results = await Promise.all(promises);
      const firstError = results.find((r) => r.error);
      if (firstError?.error) throw firstError.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // --- CREATE ---
  const createMutation = useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      if (!user?.id || !effectiveTeamId) throw new Error("Not authenticated");

      const { error } = await supabase.from("tasks").insert({
        title: input.title,
        description: input.description || null,
        priority: input.priority || "medium",
        status: "pending",
        due_date: input.dueDate || null,
        category: input.category || "other",
        assignee_id: input.assigneeId || null,
        source: "manual",
        team_id: effectiveTeamId,
        created_by: user.id,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // --- DELETE ---
  const deleteMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from("tasks")
        .delete()
        .eq("id", taskId);

      if (error) throw error;
    },
    onMutate: async (taskId: string) => {
      // Optimistic removal
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TaskType[]>(queryKey);
      queryClient.setQueryData<TaskType[]>(queryKey, (old) =>
        old ? old.filter((t) => t.id !== taskId) : []
      );
      return { previous };
    },
    onError: (_err, _taskId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateTask = useCallback(
    (task: TaskType) => updateMutation.mutate(task),
    [updateMutation]
  );

  const batchUpdateTasks = useCallback(
    (tasksToUpdate: TaskType[]) => {
      if (tasksToUpdate.length === 0) return;
      batchUpdateMutation.mutate(tasksToUpdate);
    },
    [batchUpdateMutation]
  );

  const deleteTask = useCallback(
    (taskId: string) => deleteMutation.mutate(taskId),
    [deleteMutation]
  );

  const createTask = useCallback(
    (input: CreateTaskInput) => createMutation.mutateAsync(input),
    [createMutation]
  );

  return {
    columns,
    tasks,
    updateTask,
    batchUpdateTasks,
    deleteTask,
    createTask,
    isLoading: isLoading || teamLoading,
  };
};
